import { formatDate } from '@angular/common';
import { Inject, LOCALE_ID } from '@angular/core';
import { Observable } from 'rxjs';

import { EcmDispatcherModule } from '@ecm/dispatcher';

import { Transaction } from '../enums/transaction';
import { TransactionInputs } from '../enums/transaction-inputs';
import { IdUtils } from '../utils/id.utils';

export abstract class BcoAbstractService {
    /**
     * Class name for console logs
     * @type {string}
     */
    protected abstract readonly CLASS_NAME: string;

    /**
     * Apache end point for logs collect
     * @type {string}
     */
    protected abstract readonly BCO_ENDPOINT: string;

    /**
     * Max number of logs sent by request
     * @type {number}
     */
    protected readonly BCO_PACKET_SIZE: number = 4;

    /**
     * Timeout value in millisecond fo the HTTP Request to the server
     * @type {number}
     */
    protected readonly BCO_TIMEOUT: number = 3000;

    /**
     * Transactions cache
     * @type {Map<string, Transaction>}
     */
    protected transactions: Map<string, Transaction>;

    /**
     * BCO logs cache
     * @type {Transaction[]}
     */
    protected logsQueue: Transaction[];

    /**
     * Logs sent in a failed request will be stored in this queue for later resent
     * @type {Transaction[]}
     */
    protected failedLogsQueue: Transaction[];

    public constructor(@Inject(LOCALE_ID) protected locale: string) {
        this.transactions = new Map<string, Transaction>();
        this.logsQueue = [];
        this.failedLogsQueue = [];
    }

    /**
     * Format the date according to the specified format
     * @param {Date} date The date to format
     * @returns {string} The formatted date string
     */
    protected transformDate(date: Date): string {
        return formatDate(date, 'yyyy-MM-ddTHH:mm:ss.SSS', this.locale || 'Europe/Paris');
    }

    /**
     * Begins a transaction with a service and adds it into the Map
     * @param {TransactionInputs} transactionInputs from TransactionInputs interface
     * @returns {string} transactionId of the transaction in the Map
     */
    public beginTransaction(transactionInputs: TransactionInputs): string {
        const transaction: Transaction = new Transaction();
        Object.assign(transaction, transactionInputs);
        transaction.id = IdUtils.generateUUID();
        transaction.startDate = this.transformDate(new Date(Date.now()));
        this.transactions.set(transaction.id, transaction);
        return transaction.id;
    }

    /**
     * End a transaction with a service
     * @param {string} transactionId Unique identifier of transaction
     * @param {string} status Transaction status (allowed values: "OK|KORG|KOSI|TO")
     * @param {string} code Error code if transaction status is "KORG|KOSI|TO"
     * @param {string} errorMessage Error description
     * @todo Move a part of the formating in the formatter ?
     */
    public endTransaction(transactionId: string, status: string, code: string, errorMessage: string): void {
        if (!this.transactions.has(transactionId)) {
            return;
        }

        const transaction = this.transactions.get(transactionId);
        transaction.endDate = this.transformDate(new Date(Date.now()));
        transaction.status = status;
        transaction.code = code.replace(/[;|]/g, ' ');

        let sanitizedErrorMessage: string;
        // Remove non-ascii characters (code > 127) that might not be allowed in HTTP headers
        // eslint-disable-next-line no-control-regex
        sanitizedErrorMessage = errorMessage.replace(/[^\x00-\x7F]/g, '');
        // Replace separator characters (';' and '|') that will prevent proper parsing of BCO logs
        sanitizedErrorMessage = sanitizedErrorMessage.replace(/[;|]/g, ' ');
        // Truncate message after 150 characters in case of very verbose messages
        sanitizedErrorMessage = sanitizedErrorMessage.substring(0, 150);
        transaction.errorMessage = sanitizedErrorMessage;

        this.enqueueLog(transactionId);
    }

    /**
     * Add a BCO log in CSV format
     * @param {string} transactionId Unique identifier of transaction to be converted into a BCO log
     */
    private enqueueLog(transactionId: string): void {
        const transaction: Transaction = this.transactions.get(transactionId);
        const toString = (obj) =>
            Object.entries(obj)
                .map(([k, v]) => `${JSON.stringify(k)}: ${JSON.stringify(v)}`)
                .join(', ')
                .replace(/\s+/g, ' ');
        EcmDispatcherModule.sendConsoleInfo(
            this.CLASS_NAME,
            `Enqueuing new ready BCO transaction : {${toString(transaction)}}`,
        );
        this.logsQueue.push(transaction);

        this.transactions.delete(transactionId);
    }

    /**
     * - Unqueue cached logs, and send them to Apache;
     * - Add the logs to the failed logs queue if error occurs in the process;
     * @param {boolean} [deprecatedSync] DEPRECATED, not used anymore (was used for synchronous call)
     * @return {Observable<boolean>} A shared Observable to know when logs are sent to the server
     * The returned Observable will always publish a true value, even if a request is in error
     */
    public abstract flushLogs(): Observable<boolean>;

    /**
     * Take logs from failed logs queue and add retry in comment if needed to detect them in BCO log
     */
    protected reinjectFailedLogsInList(): void {
        const failedLogs = this.failedLogsQueue.splice(0).map((failedLog: Transaction) => {
            failedLog.commentary = 'retry';
            return failedLog;
        });

        this.logsQueue.push(...failedLogs);
    }
}
